import TabContentWrapper from 'pages/Profile/components/TabContentWrapper';
import styles from './styles.module.scss';
import { TEAM_ADMIN, TEAM_BASIC, TEAM_OWNER } from 'settings/constants/roles';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getAgentTeamIsActiveSelector,
  getAgentTeamRoleSelector,
} from 'store/selectors/agentTeamDetail';
import { getAgentDetailsSelector } from 'store/selectors/agentDetail';
import { Icons } from '../../components/icons';
import classNames from 'classnames';
import { Select, Option } from 'components-antd';
import { getTeamClients, getTeamMemberClients } from 'api/teamList';
import { showErrorMessage } from 'helpers';
import { Spinner, Button } from 'components';
import TeamMemberClientsTable from './components/TeamMemberClientsTable';
import EmptyTable from './components/EmptyTable/Index';
import { AssignClientsModal } from './components/AssignClientsModal';
import { reassignShareClientsEffect, requestGetTeamListEffect } from 'store/effects';
import { ReassignShareModal } from 'pages/Profile/components/ReassignShareModal';
import { getUserId } from 'store/selectors/user';

enum ClientTypeSelector {
  all = 'All',
  active = 'Active',
  inactive = 'Inactive',
  pending = 'Pending',
}

const Clients = () => {
  const dispatch = useDispatch();
  const isTeamAgentActive = useSelector(getAgentTeamIsActiveSelector);
  const [multiSelect, setMultiSelect] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [teamClientsLoading, setTeamClientsLoading] = useState<boolean>(false);
  const [reassigning, setReassigning] = useState<boolean>(false);
  const countRef = useRef<number>(0);
  const [showAssignClientsModal, setShowAssignClientsModal] = useState<boolean>(false);
  const [showReassignmentModal, setShowReassignmentModal] = useState<boolean>(false);
  const [clientList, setClientList] = useState<any[]>([]);
  const [teamClients, setTeamClients] = useState<any>([]);
  const [reassignOrShare, setReassignOrShare] = useState<'Reassign' | 'Share'>('Reassign');
  const [selectedClientIds, setSelectedClientIds] = useState<number[]>([]);
  const details = useSelector(getAgentDetailsSelector);
  const contact = details?.data || {};
  const agentRole = useSelector(getAgentTeamRoleSelector);
  const userId = useSelector(getUserId);
  const limitedAccess = userId === contact.Id && agentRole === TEAM_BASIC;
  const [selectedClientType, setSelectedClientType] = useState<ClientTypeSelector>(
    ClientTypeSelector.all,
  );

  useEffect(() => {
    fetchTeamClients();
    fetchTeamMemberClients();
    dispatch(requestGetTeamListEffect({}, { silent: true }));
  }, []);

  const fetchTeamClients = async () => {
    setLoading(true);
    try {
      const list = await getTeamClients();
      setTeamClients(list?.data || []);
    } catch (e) {
      showErrorMessage('Unable to fetch team clients');
    }
    setLoading(false);
  };

  const fetchTeamMemberClients = async () => {
    setTeamClientsLoading(true);
    try {
      const clients = await getTeamMemberClients(null, { agentId: contact?.Id });
      setClientList(clients?.data || []);
    } catch (e) {
      showErrorMessage(`Unable to fetch team member's clients.`);
    }
    setTeamClientsLoading(false);
  };

  const onClientReassignment = () => {
    fetchTeamClients();
    fetchTeamMemberClients();
  };

  const clientSelectorOptions = [
    {
      label: ClientTypeSelector.all,
      value: ClientTypeSelector.all,
    },
    {
      label: ClientTypeSelector.active,
      value: ClientTypeSelector.active,
    },
    {
      label: ClientTypeSelector.inactive,
      value: ClientTypeSelector.inactive,
    },
    {
      label: ClientTypeSelector.pending,
      value: ClientTypeSelector.pending,
    },
  ];

  const onClientSelectorChange = (value) => {
    setSelectedClientType(value);
  };

  const getClientSelector = () => (
    <Select
      size={'large'}
      defaultValue={ClientTypeSelector.all}
      popupClassName={styles.clientSelectorPopup}
      onChange={onClientSelectorChange}
      placement={'bottomLeft'}
      dropdownIconColor={'#262626'}
      suffixIcon={<Icons variant={Icons.DROPDOWN} />}
      disabled={clientList.length === 0}
    >
      {clientSelectorOptions.map(({ label }, index) => (
        <Option key={index} value={label}>
          <span className={styles.label}>{label}</span>
        </Option>
      ))}
    </Select>
  );

  const getFilteredClients = () => {
    if (selectedClientType === ClientTypeSelector.all) {
      countRef.current = clientList?.length ?? 0;
      return clientList;
    }

    if (selectedClientType === ClientTypeSelector.active) {
      const clients = clientList.filter(
        (client) => client.InvitationStatus === 'Accepted' && client.IsActive,
      );
      countRef.current = clients?.length ?? 0;
      return clients;
    }

    if (selectedClientType === ClientTypeSelector.inactive) {
      const clients = clientList.filter(
        (client) => client.InvitationStatus === 'Accepted' && !client.IsActive,
      );
      countRef.current = clients?.length ?? 0;
      return clients;
    }

    if (selectedClientType === ClientTypeSelector.pending) {
      const clients = clientList.filter((client) => client.InvitationStatus === 'Pending');
      countRef.current = clients?.length ?? 0;
      return clients;
    }
    countRef.current = 0;
    return [];
  };

  const handleReassignClick = (mode: 'Reassign' | 'Share', clientId?: number) => {
    if (!mode) return;

    setReassignOrShare(mode);

    if (clientId) {
      setSelectedClientIds([clientId]);
      setShowReassignmentModal(true);
    } else if (selectedClientIds.length) setShowReassignmentModal(true);
  };

  const handleReassignmentModalClose = (reassigned: boolean) => {
    if (reassigned) {
      setMultiSelect(false);
      setSelectedClientIds([]);
      fetchTeamMemberClients();
      fetchTeamClients();
    } else if (!multiSelect) setSelectedClientIds([]);

    setShowReassignmentModal(false);
  };

  const onReassign = (agentIds) => {
    setReassigning(true);

    dispatch(
      reassignShareClientsEffect(
        {
          clientIds: [...selectedClientIds],
          agentIds,
        },
        { type: 'reassign' },
        (err) => {
          if (!err) {
            handleReassignmentModalClose(true);
          }

          setReassigning(false);
        },
      ),
    );
  };

  const getClientsForReassignment = () => {
    return clientList
      .filter((client) => selectedClientIds.includes(client.Id))
      .map((client) => {
        const assignedAgentIds = teamClients?.find(
          (item) => item.Id === client.Id,
        )?.AssignedAgentIds;
        return {
          Id: client.Id,
          FirstName: client.FirstName,
          LastName: client.LastName,
          AvatarUrl: client.AvatarUrl,
          AssignedAgentIds: assignedAgentIds?.length ? assignedAgentIds : [contact?.Id],
        };
      });
  };

  const assignClientsClickHandler = () => setShowAssignClientsModal(true);

  if (loading || teamClientsLoading)
    return (
      <TabContentWrapper setMargin contentHeightFull>
        <div className={styles.clients}>
          <div className={classNames(styles.content, styles.contentLoading)}>
            <Spinner />
          </div>
        </div>
      </TabContentWrapper>
    );
  else if (!loading && !teamClientsLoading && !clientList.length)
    return (
      <TabContentWrapper setMargin contentHeightFull>
        <div className={styles.clients}>
          <div className={styles.content}>
            <EmptyTable
              firstName={contact?.FirstName}
              lastName={contact?.LastName}
              clickHandler={assignClientsClickHandler}
            />
          </div>
        </div>
        <AssignClientsModal
          contact={contact}
          isOpen={showAssignClientsModal}
          onClose={() => setShowAssignClientsModal(false)}
          onClientReassignment={onClientReassignment}
          teamClients={teamClients}
        />
      </TabContentWrapper>
    );

  return (
    <TabContentWrapper setMargin contentHeightFull>
      <div className={styles.clients}>
        <div className={styles.header}>
          <div className={styles.left}>{`Clients (${countRef?.current ?? 0})`}</div>
          <div className={styles.right}>
            {getClientSelector()}
            {isTeamAgentActive ? (
              <>
                {(agentRole === TEAM_OWNER || agentRole === TEAM_ADMIN) && (
                  <div className={styles.icon} onClick={assignClientsClickHandler}>
                    <Icons variant={Icons.ADD_CLIENT} color="#262626" />
                  </div>
                )}
                {limitedAccess ? null : (
                  <div
                    className={classNames(
                      styles.icon,
                      multiSelect ? styles.iconSelected : undefined,
                    )}
                    onClick={() => {
                      if (!clientList.length) return;
                      if (multiSelect) setSelectedClientIds([]);
                      setMultiSelect((prev) => !prev);
                    }}
                  >
                    <Icons variant={Icons.MULTISELECT} color="#262626" />
                  </div>
                )}
              </>
            ) : null}
          </div>
        </div>
        <div className={styles.content}>
          <TeamMemberClientsTable
            clients={getFilteredClients()}
            multiSelectMode={multiSelect}
            selectedClientIds={selectedClientIds}
            setSelectedClientIds={setSelectedClientIds}
            handleReassignClick={handleReassignClick}
            limitedAccess={limitedAccess}
          />
          {multiSelect && (
            <div className={styles.footer}>
              <div className={styles.left}>{`${selectedClientIds.length} Client${
                selectedClientIds.length > 1 ? 's' : ''
              } Selected`}</div>
              <div className={styles.right}>
                <Button
                  type="submit"
                  className={classNames(styles.reassignButton, styles.sharedButton)}
                  title={'Reassign'}
                  onClick={() => handleReassignClick('Reassign')}
                  disabled={selectedClientIds.length === 0}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      <AssignClientsModal
        contact={contact}
        isOpen={showAssignClientsModal}
        onClose={() => setShowAssignClientsModal(false)}
        onClientReassignment={onClientReassignment}
        teamClients={teamClients}
      />
      <ReassignShareModal
        isOpen={showReassignmentModal}
        modalType={reassignOrShare}
        onReassignShare={onReassign}
        onClose={() => handleReassignmentModalClose(false)}
        clients={getClientsForReassignment()}
        loading={reassigning}
        loadingAssignees={false}
        contactAgentId={reassignOrShare === 'Share' ? contact?.Id : 0}
      />
    </TabContentWrapper>
  );
};

export default Clients;
